import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { formatDate } from '../utils/dateFormat';

const US_TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

function Section({ title, actions, children }) {
  return (
    <div className="bg-white rounded-xl shadow-card">
      <div className="px-5 py-4 border-b border-secondary-100 flex items-center justify-between">
        <h3 className="font-semibold text-secondary-900">{title}</h3>
        {actions}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-secondary-400 mb-0.5">{label}</p>
      <p className="text-sm text-secondary-800 font-medium">{value || '--'}</p>
    </div>
  );
}

const EMPTY_CONTACT = {
  name: '',
  relationship: '',
  phone: '',
  email: '',
  priority: 1,
  notifyEmail: true,
  notifySms: true,
};

export default function PatientDetail() {
  const { id } = useParams();
  const [patient, setPatient] = useState(null);
  const [devices, setDevices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit patient state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Add contact state
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState('');

  const fetchPatient = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients/${id}`);
      setPatient(data.patient);
      setDevices(data.devices || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchContacts = useCallback(async () => {
    try {
      const { data } = await api.get(`/patients/${id}/emergency-contacts`);
      setContacts(data.data || data.contacts || []);
    } catch {
      // silent
    }
  }, [id]);

  useEffect(() => { fetchPatient(); fetchContacts(); }, [fetchPatient, fetchContacts]);

  // Start editing
  const startEditing = () => {
    setEditForm({
      firstName: patient.firstName || '',
      lastName: patient.lastName || '',
      dateOfBirth: patient.dateOfBirth ? patient.dateOfBirth.slice(0, 10) : '',
      address: patient.address || '',
      city: patient.city || '',
      state: patient.state || '',
      zipCode: patient.zipCode || '',
      phone: patient.phone || '',
      email: patient.email || '',
      medicalNotes: patient.medicalNotes || '',
      emergencyInstructions: patient.emergencyInstructions || '',
      timezone: patient.timezone || 'America/Chicago',
    });
    setEditError('');
    setEditing(true);
  };

  const handleEditChange = (e) => {
    setEditForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    setEditError('');
    try {
      const { data } = await api.put(`/patients/${id}`, editForm);
      setPatient(data.patient);
      setEditing(false);
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update patient');
    } finally {
      setEditSaving(false);
    }
  };

  // Contact handlers
  const handleContactChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContactForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSaving(true);
    setContactError('');
    try {
      await api.post(`/patients/${id}/emergency-contacts`, {
        ...contactForm,
        priority: Number(contactForm.priority),
      });
      setContactForm(EMPTY_CONTACT);
      setShowContactForm(false);
      fetchContacts();
    } catch (err) {
      setContactError(err.response?.data?.error || 'Failed to add contact');
    } finally {
      setContactSaving(false);
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Delete this emergency contact?')) return;
    try {
      await api.delete(`/patients/${id}/emergency-contacts/${contactId}`);
      fetchContacts();
    } catch {
      // silent
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-secondary-400">Loading...</div>;
  if (!patient) return <div className="text-red-500">Patient not found</div>;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link to="/patients" className="text-secondary-400 hover:text-secondary-600">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">{patient.firstName} {patient.lastName}</h1>
          <p className="text-sm text-secondary-500">{patient.email || 'No email'}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Patient Info */}
        <Section
          title="Patient Info"
          actions={
            !editing && (
              <button onClick={startEditing}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                Edit
              </button>
            )
          }
        >
          {editing ? (
            <form onSubmit={handleEditSave} className="space-y-4">
              {editError && <p className="text-sm text-red-600">{editError}</p>}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">First Name *</label>
                  <input name="firstName" value={editForm.firstName} onChange={handleEditChange} required
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Last Name *</label>
                  <input name="lastName" value={editForm.lastName} onChange={handleEditChange} required
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Date of Birth</label>
                  <input name="dateOfBirth" type="date" value={editForm.dateOfBirth} onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Phone</label>
                  <input name="phone" value={editForm.phone} onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Email</label>
                  <input name="email" type="email" value={editForm.email} onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Address</label>
                  <input name="address" value={editForm.address} onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">City</label>
                  <input name="city" value={editForm.city} onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">State</label>
                  <input name="state" value={editForm.state} onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Zip Code</label>
                  <input name="zipCode" value={editForm.zipCode} onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Timezone</label>
                  <select name="timezone" value={editForm.timezone} onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none">
                    {US_TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Medical Notes</label>
                <textarea name="medicalNotes" value={editForm.medicalNotes} onChange={handleEditChange} rows={3}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none" />
              </div>
              <div>
                <label className="block text-xs text-secondary-500 mb-1">Emergency Instructions</label>
                <textarea name="emergencyInstructions" value={editForm.emergencyInstructions} onChange={handleEditChange} rows={3}
                  className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none" />
              </div>
              <div className="flex gap-2 justify-end">
                <button type="button" onClick={() => setEditing(false)}
                  className="px-4 py-2 text-sm border border-secondary-200 rounded-lg hover:bg-secondary-50 transition">Cancel</button>
                <button type="submit" disabled={editSaving}
                  className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50">
                  {editSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <Field label="First Name" value={patient.firstName} />
              <Field label="Last Name" value={patient.lastName} />
              <Field label="Date of Birth" value={formatDate(patient.dateOfBirth)} />
              <Field label="Phone" value={patient.phone} />
              <Field label="Email" value={patient.email} />
              <Field label="Address" value={patient.address} />
              <Field label="City" value={patient.city} />
              <Field label="State" value={patient.state} />
              <Field label="Zip Code" value={patient.zipCode} />
              <Field label="Timezone" value={patient.timezone} />
              <Field label="Medical Notes" value={patient.medicalNotes} />
              <Field label="Emergency Instructions" value={patient.emergencyInstructions} />
            </div>
          )}
        </Section>

        {/* Linked Devices */}
        <Section title="Linked Devices">
          {devices.length === 0 ? (
            <p className="text-sm text-secondary-400">No devices linked to this patient</p>
          ) : (
            <div className="space-y-2">
              {devices.map((d) => (
                <Link key={d.id} to={`/devices/${d.id}`}
                  className="flex items-center justify-between py-2 px-3 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition">
                  <div>
                    <span className="text-sm font-medium text-secondary-800">{d.device_id}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    d.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                  }`}>{d.status}</span>
                </Link>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Emergency Contacts */}
      <Section
        title="Emergency Contacts"
        actions={
          contacts.length < 5 && (
            <button onClick={() => setShowContactForm((v) => !v)}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              {showContactForm ? 'Cancel' : '+ Add Contact'}
            </button>
          )
        }
      >
        {/* Add Contact Form */}
        {showContactForm && (
          <div className="mb-5 pb-5 border-b border-secondary-100">
            {contactError && <p className="text-sm text-red-600 mb-3">{contactError}</p>}
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Name *</label>
                  <input name="name" value={contactForm.name} onChange={handleContactChange} required
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Relationship *</label>
                  <input name="relationship" value={contactForm.relationship} onChange={handleContactChange} required
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Phone *</label>
                  <input name="phone" value={contactForm.phone} onChange={handleContactChange} required
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Email</label>
                  <input name="email" type="email" value={contactForm.email} onChange={handleContactChange}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-secondary-500 mb-1">Priority (1-5)</label>
                  <input name="priority" type="number" min={1} max={5} value={contactForm.priority} onChange={handleContactChange}
                    className="w-full px-3 py-2 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-secondary-700">
                  <input type="checkbox" name="notifyEmail" checked={contactForm.notifyEmail} onChange={handleContactChange}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500" />
                  Notify via Email
                </label>
                <label className="flex items-center gap-2 text-sm text-secondary-700">
                  <input type="checkbox" name="notifySms" checked={contactForm.notifySms} onChange={handleContactChange}
                    className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500" />
                  Notify via SMS
                </label>
              </div>
              <div className="flex justify-end">
                <button type="submit" disabled={contactSaving}
                  className="px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm font-medium disabled:opacity-50">
                  {contactSaving ? 'Saving...' : 'Add Contact'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Contact List */}
        {contacts.length === 0 ? (
          <p className="text-sm text-secondary-400">No emergency contacts</p>
        ) : (
          <div className="space-y-2">
            {contacts.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-3 px-4 bg-secondary-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-secondary-800">{c.name}</span>
                    <span className="text-xs bg-secondary-200 text-secondary-600 px-2 py-0.5 rounded-full">{c.relationship}</span>
                    <span className="text-xs text-secondary-400">Priority {c.priority}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-xs text-secondary-500">{c.phone}</span>
                    {c.email && <span className="text-xs text-secondary-500">{c.email}</span>}
                    {c.notifyEmail && <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">Email</span>}
                    {c.notifySms && <span className="text-xs bg-green-50 text-green-600 px-1.5 py-0.5 rounded">SMS</span>}
                  </div>
                </div>
                <button onClick={() => deleteContact(c.id)}
                  className="ml-3 text-secondary-400 hover:text-red-600 transition" title="Delete contact">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
